// ── SmartPluvia Planner — Main Orchestrator ─────────────────────────
// Imports modules and wires initialization, rendering, events together.

const LOCAL_AUTOSAVE_KEY = 'smartpluvia-autosave';
const LOCAL_AUTOSAVE_INTERVAL_MS = 5000;
const DRAFT_RETRY_MS = 30000;
const PROJECT_DATA_VERSION = 4;

import { loadCatalogs } from '../catalog/catalog-loader.ts';
import { autoPlaceSprinklers, autoAssignSprinklersToZones as zoneByPosition, findClosestArc, getArcData, migrateSprinkler, migrateZone } from '../calculations/logic.ts';
import { LANDSCAPE_OBJECTS } from '../catalog/catalog-data.ts';
import { t, initI18n, interpolate } from '../shared/i18n.ts';
import { showConfirm } from '../shared/custom-dialog.ts';
import { showToast, authenticatedFetch } from '../shared/utils.ts';
import { trackExportPdf, trackExportSvg } from '../shared/analytics.ts';
import { isAuthenticated } from '../shared/auth.ts';
import { getUnits } from '../shared/units.ts';
import { startOnboarding } from './onboarding.ts';
import { featureEnabled, loadFeatureFlags } from '../shared/features.ts';
import { initFeedback } from '../pages/feedback.ts';
import { api_v1_project_pdf_path, api_v1_project_pdf_status_path } from '../shared/api-routes';
import { createCompass, updateCompassLabels } from './compass.ts';

import {
  state, clearSelection, createSprinkler, saveState, undo, redo,
  downloadBlob,
  loadProjectFromAPI, saveProjectToAPI, startCloudAutoSave, flushCloudAutoSave,
  getCurrentProjectId, ensureCloudDraft, setFirstMutationHook,
  deriveCoverageView, syncCoverageViewControl, normalizeStoredElements,
} from './state.ts';

import {
  $, $$, svg, container,
  screenToMeters, snapMeters, zoomAt, zoomToFit,
} from './canvas.ts';

import {
  autoAssignSprinklersToZones,
  renderGrid, renderManualGuides, renderBoundary, renderCoverage, renderHeatmap,
  renderZones, renderPipes, renderDripLines, renderTrees, renderValveObjects,
  renderWeatherStations, renderSprinklers, renderTemp,
  renderSprinklerList, renderPipeList, renderDripList, renderZoneList,
  renderTreeList, renderValveObjList, renderWeatherStationList,
  updateStats, updatePropertiesPanel, updateZonePropertiesPanel,
  updatePipePropertiesPanel, updateDripPropertiesPanel,
  updateTreePropertiesPanel, updateValveObjPropertiesPanel,
  updateWeatherStationPropertiesPanel,
  buildObjectTypeSelect, buildDripModelSelect, addGuide,
} from './render.ts';

import { updateToolbarPreset, setMode } from './toolbar.ts';
import { applyFieldUnits, readField, showField } from './field-units.ts';

import {
  onCanvasMouseDown, onCanvasMouseMove, onCanvasMouseUp, onCanvasWheel,
  onKeyDown, setEventCallbacks,
} from './events.ts';

// ── renderAll: central render function ─────────────────────────────
function renderAll() {
  renderGrid();
  renderManualGuides();
  renderBoundary();
  renderTrees();
  // The unified coverage layer dispatches on `state.coverageView`:
  // 'rings' draws translucent reach circles, 'heatmap' draws the
  // precipitation raster, 'off' clears both. Both render functions
  // bail out internally if their mode is not active, so calling both
  // unconditionally is safe and cheap.
  renderCoverage();
  renderHeatmap();
  renderZones();
  renderPipes();
  renderDripLines();
  renderValveObjects();
  renderWeatherStations();
  renderSprinklers();
  renderTemp();
  renderSprinklerList({ selectSprinkler, renderAll });
  renderPipeList(renderAll);
  renderDripList(renderAll);
  renderZoneList(renderAll);
  renderTreeList({ selectTree, renderAll });
  renderValveObjList({ selectValveObj, renderAll });
  renderWeatherStationList({ selectWeatherStation, renderAll });
  updateStats();
  updatePropertiesPanel();
  updateZonePropertiesPanel({ selectSprinkler });
  updatePipePropertiesPanel();
  updateDripPropertiesPanel();
  updateTreePropertiesPanel();
  updateValveObjPropertiesPanel();
  updateWeatherStationPropertiesPanel();
}

// ── Selection helpers ──────────────────────────────────────────────
function selectSprinkler(id: number) {
  clearSelection();
  state.selectedSprinkler = id;
  renderAll();
}

function selectTree(id: number) {
  clearSelection();
  state.selectedTree = id;
  renderAll();
}

function selectValveObj(id: number) {
  clearSelection();
  state.selectedValveObj = id;
  renderAll();
}

function selectWeatherStation(id: number) {
  clearSelection();
  state.selectedWeatherStation = id;
  renderAll();
}

// ── Wire event callbacks ──────────────────────────────────────────
setEventCallbacks({ renderAll, selectSprinkler, selectTree, selectValveObj, selectWeatherStation });

// ── Export SVG ────────────────────────────────────────────────────
//
// Resolves CSS custom properties to literal hex values inside the cloned
// SVG before serialization (#1321). Without this, the exported file
// references unresolved `var(--ink)` / `var(--paper)` etc. — every viewer
// (Inkscape, Figma import, raw Chrome `<img>` embedding) renders default
// black for the unresolved values.
const SVG_TOKEN_NAMES = [
  "--paper", "--ink", "--blue", "--rule", "--grid",
  "--zone-purple", "--pipe-blue", "--head-red", "--coverage", "--label-mute",
] as const;

function buildResolvedTokenStyle(): string {
  const root = getComputedStyle(document.documentElement);
  const decls = SVG_TOKEN_NAMES
    .map((name) => {
      const value = root.getPropertyValue(name).trim();
      return value ? `  ${name}: ${value};` : null;
    })
    .filter((d): d is string => d !== null)
    .join("\n");
  return `:root {\n${decls}\n}\n`;
}

function exportSVG() {
  const clone = svg().cloneNode(true) as SVGElement;
  const temp = clone.querySelector("#tempLayer");
  if (temp) temp.replaceChildren();

  // Prepend a <defs><style> with resolved tokens. Browsers resolve
  // var(--ink) against the standalone file's :root rule, so colors
  // match the live canvas regardless of the host environment.
  const styleEl = document.createElementNS("http://www.w3.org/2000/svg", "style");
  styleEl.textContent = buildResolvedTokenStyle();
  const defs = clone.querySelector("defs") || (() => {
    const d = document.createElementNS("http://www.w3.org/2000/svg", "defs");
    clone.insertBefore(d, clone.firstChild);
    return d;
  })();
  defs.insertBefore(styleEl, defs.firstChild);

  const serializer = new XMLSerializer();
  const source = '<?xml version="1.0" encoding="UTF-8"?>\n' + serializer.serializeToString(clone);
  downloadBlob(new Blob([source], { type: "image/svg+xml;charset=utf-8" }), "smartpluvia-plan.svg");
  trackExportSvg();
}

// ── Export PDF (async) ─────────────────────────────────────────────
// Enqueue a PDF generation job, then poll the status endpoint every 3s
// until it flips to ready. On ready we follow the download URL through a
// download link, which saves the attachment without leaving the planner.
//
// Previously the planner offered no PDF export button at all and the
// backend's sync GET endpoint blocked a Puma worker on Grover cold start
// (~10-30s). The new async flow keeps both server and client responsive.
const PDF_POLL_INTERVAL_MS = 3000;
const PDF_POLL_TIMEOUT_MS = 120000;

/** Two heads closer than this are the same point for placement purposes —
 *  well under the metres of spacing auto-place works with. */
const OCCUPIED_TOLERANCE_M = 0.25;

/** Resolve an i18n key or fall back to the english string. The shared t()
 *  returns the key itself on miss — and every key misses when the locale
 *  request failed — so the toast still says something readable. */
function tf(key: string, fallback: string, params?: Record<string, string | number>): string {
  const out = t(key, params);
  // The fallback needs the same placeholder pass a resolved translation gets,
  // or a miss ships the template verbatim — "Placed {count} heads".
  return out === key ? interpolate(fallback, params) : out;
}

async function exportPDF() {
  const projectId = getCurrentProjectId();
  if (!projectId) {
    showToast(tf("toolbar.pdfExportNoProject", "Save the project first"), "warning");
    return;
  }
  if (!isAuthenticated()) {
    showToast(tf("toolbar.pdfLoginRequired", "Please sign in"), "warning");
    return;
  }

  showToast(tf("toolbar.pdfGenerating", "Generating PDF… this may take 10–30 s"), "success");

  try {
    const resp = await authenticatedFetch(api_v1_project_pdf_path(projectId), {
      method: "POST",
      body: {},
    });
    if (resp.status === 429) {
      const retryAfter = resp.headers.get("Retry-After") || "60";
      showToast(
        tf("toolbar.pdfRateLimited", "Too many requests — try again in {seconds} s", { seconds: retryAfter }),
        "warning"
      );
      return;
    }
    if (!resp.ok) {
      showToast(tf("toolbar.pdfEnqueueFailed", "Failed to start PDF generation"), "error");
      return;
    }
  } catch (err) {
    console.error("PDF enqueue failed:", err);
    showToast(tf("toolbar.pdfEnqueueFailed", "Failed to start PDF generation"), "error");
    return;
  }

  // Poll until ready or timeout.
  const startedAt = Date.now();
  const poll = async () => {
    if (Date.now() - startedAt > PDF_POLL_TIMEOUT_MS) {
      showToast(tf("toolbar.pdfTimeout", "PDF generation took too long — please retry"), "error");
      return;
    }
    try {
      const statusResp = await authenticatedFetch(api_v1_project_pdf_status_path(projectId));
      if (!statusResp.ok) {
        showToast(tf("toolbar.pdfStatusFailed", "Failed to check PDF status"), "error");
        return;
      }
      const body = await statusResp.json();
      if (body.status === "ready" && body.download_url) {
        // Not window.open(): the click that asked for the file is 10-30 s old
        // by now, its user activation has lapsed, and popup blockers drop the
        // window — the toast said "ready" and nothing arrived. The endpoint
        // answers with Content-Disposition: attachment, so a download link
        // saves the file without leaving the planner.
        const link = document.createElement("a");
        link.href = body.download_url;
        link.download = "";
        document.body.appendChild(link);
        link.click();
        link.remove();
        showToast(tf("toolbar.pdfReady", "PDF is ready!"), "success");
        trackExportPdf();
        return;
      }
      if (body.status === "failed") {
        showToast(tf("toolbar.pdfFailed", "PDF generation failed"), "error");
        return;
      }
      setTimeout(poll, PDF_POLL_INTERVAL_MS);
    } catch (err) {
      console.error("PDF status poll failed:", err);
      setTimeout(poll, PDF_POLL_INTERVAL_MS);
    }
  };
  setTimeout(poll, PDF_POLL_INTERVAL_MS);
}

// ── Property change handlers ──────────────────────────────────────
function showPlotSize() {
  showField($("#plotWidth"), state.plotWidth, "length");
  showField($("#plotHeight"), state.plotHeight, "length");
}

function applyPropertyChange() {
  const sp = state.sprinklers.find((s) => s.id === state.selectedSprinkler);
  if (!sp) return;

  saveState();
  const newModel = $("#sprinklerModel").value;
  const modelChanged = newModel !== sp.model;

  sp.model = newModel;
  sp.name = $("#sprinklerName").value || sp.name;
  const newX = readField($("#sprinklerX"), "length");
  const newY = readField($("#sprinklerY"), "length");
  sp.x = isNaN(newX) ? sp.x : newX;
  sp.y = isNaN(newY) ? sp.y : newY;
  sp.rotation = parseInt($("#sprinklerRotation").value) || 0;

  if (modelChanged) {
    sp.arc = findClosestArc(sp.model, sp.arc);
    sp.radius = getArcData(sp.model, sp.arc).radius;
  } else {
    const rawArc = parseInt($("#sprinklerArc").value) || sp.arc;
    sp.arc = findClosestArc(sp.model, rawArc);
    const manualRadius = readField($("#sprinklerRadius"), "length");
    sp.radius = manualRadius || getArcData(sp.model, sp.arc).radius;
  }

  // Zone assignment
  const zoneVal = $("#sprinklerZone").value;
  if (zoneVal) {
    sp.zoneId = parseInt(zoneVal);
    sp._manualZone = true;
  } else {
    sp.zoneId = null;
    sp._manualZone = false;
  }

  // Spray body assignment
  const bodyVal = $("#sprinklerBody").value;
  sp.bodyModel = bodyVal || null;

  renderAll();
}

function applyZonePropertyChange() {
  const zone = state.zones.find((z) => z.id === state.selectedZone);
  if (!zone) return;
  saveState();
  zone.name = $("#zoneName").value || zone.name;
  zone.valveModel = $("#zoneValve").value || null;
  renderAll();
}

function applyPipePropertyChange() {
  const pipe = state.pipes.find((p) => p.id === state.selectedPipe);
  if (!pipe) return;
  saveState();
  pipe.name = $("#pipeName").value || pipe.name;
  pipe.diameter = parseInt($("#pipeDiameter").value) || pipe.diameter;
  const zoneVal = $("#pipeZone").value;
  pipe.zoneId = zoneVal ? parseInt(zoneVal) : null;
  renderAll();
}

function applyTreePropertyChange() {
  const tree = state.trees.find((t) => t.id === state.selectedTree);
  if (!tree) return;
  saveState();
  tree.name = $("#treeName").value || tree.name;

  const newType = $("#treeType").value;
  if (newType && newType !== tree.type) {
    const oldDef = LANDSCAPE_OBJECTS[tree.type];
    const newDef = LANDSCAPE_OBJECTS[newType];
    tree.type = newType;
    if (newDef) {
      tree.shape = newDef.shape;
      if (newDef.shape === 'rect' && (!oldDef || oldDef.shape !== 'rect')) {
        tree.width = newDef.defaultWidth || 2;
        tree.length = newDef.defaultLength || 2;
        tree.rotation = tree.rotation || 0;
      } else if (newDef.shape === 'circle' && (!oldDef || oldDef.shape !== 'circle')) {
        tree.radius = newDef.defaultRadius || 1.5;
      }
    }
  }

  const newTreeX = readField($("#treeX"), "length");
  const newTreeY = readField($("#treeY"), "length");
  tree.x = isNaN(newTreeX) ? tree.x : newTreeX;
  tree.y = isNaN(newTreeY) ? tree.y : newTreeY;

  const def = LANDSCAPE_OBJECTS[tree.type];
  const shape = (def && def.shape) || tree.shape || 'circle';
  if (shape === 'rect') {
    const w = readField($("#objWidth"), "length");
    const l = readField($("#objLength"), "length");
    const r = parseFloat($("#objRotation").value);
    tree.width = isNaN(w) || w <= 0 ? tree.width : w;
    tree.length = isNaN(l) || l <= 0 ? tree.length : l;
    tree.rotation = isNaN(r) ? tree.rotation : r;
  } else {
    const newTreeR = readField($("#treeRadius"), "length");
    tree.radius = isNaN(newTreeR) || newTreeR <= 0 ? tree.radius : newTreeR;
  }

  if (tree.type === "waterSource") {
    const flowRate = readField($("#wsFlowRate"), "flow");
    const pressure = readField($("#wsPressure"), "pressure");
    const sourceType = $("#wsSourceType").value;
    const elevation = readField($("#wsElevation"), "length");
    if (!tree.waterSourceProps) tree.waterSourceProps = { flow_rate: 30, pressure: 3.0, source_type: "municipal", elevation: 0 };
    if (!isNaN(flowRate) && flowRate > 0) tree.waterSourceProps.flow_rate = flowRate;
    if (!isNaN(pressure) && pressure > 0) tree.waterSourceProps.pressure = pressure;
    if (sourceType) tree.waterSourceProps.source_type = sourceType;
    if (!isNaN(elevation)) tree.waterSourceProps.elevation = elevation;
  }

  renderAll();
}

function applyValveObjPropertyChange() {
  const vo = state.valveObjects.find((v) => v.id === state.selectedValveObj);
  if (!vo) return;
  saveState();
  vo.name = $("#valveObjName").value || vo.name;
  const newVoX = readField($("#valveObjX"), "length");
  const newVoY = readField($("#valveObjY"), "length");
  vo.x = isNaN(newVoX) ? vo.x : newVoX;
  vo.y = isNaN(newVoY) ? vo.y : newVoY;
  vo.model = $("#valveObjModel").value || vo.model;
  const zoneVal = $("#valveObjZone").value;
  vo.zoneId = zoneVal ? parseInt(zoneVal) : null;
  // Sync zone valveModel when valve is assigned to zone
  if (vo.zoneId) {
    const zone = state.zones.find((z) => z.id === vo.zoneId);
    if (zone) zone.valveModel = vo.model;
  }
  renderAll();
}

function applyWeatherStationPropertyChange() {
  const ws = state.weatherStations.find((w) => w.id === state.selectedWeatherStation);
  if (!ws) return;
  saveState();
  ws.name = $("#wsName").value || ws.name;
  const newWsX = readField($("#wsX"), "length");
  const newWsY = readField($("#wsY"), "length");
  ws.x = isNaN(newWsX) ? ws.x : newWsX;
  ws.y = isNaN(newWsY) ? ws.y : newWsY;
  ws.model = $("#wsModel").value || ws.model;
  renderAll();
}

function applyDripPropertyChange() {
  const dl = state.dripLines.find((d) => d.id === state.selectedDripLine);
  if (!dl) return;
  saveState();
  dl.name = $("#dripLineName").value || dl.name;
  dl.model = $("#dripLineModel").value || dl.model;
  const zoneVal = $("#dripLineZone").value;
  dl.zoneId = zoneVal ? parseInt(zoneVal) : null;
  renderAll();
}

// ── Initialize ─────────────────────────────────────────────────────
function init() {
  $$(".tool-btn").forEach((btn) => {
    btn.addEventListener("click", () => setMode((btn as HTMLElement).dataset.mode!));
  });

  // Populate landscape object type selects
  const activeObjSel = $("#activeObjectType");
  const treeTypeSel = $("#treeType");
  if (activeObjSel) buildObjectTypeSelect(activeObjSel, "tree");
  if (treeTypeSel) buildObjectTypeSelect(treeTypeSel, "tree");

  svg().addEventListener("mousedown", onCanvasMouseDown);
  svg().addEventListener("mousemove", onCanvasMouseMove);
  // `mouseup` on `window`, not on the SVG: releasing the button over the
  // sidebar, the context bar, a flyout or outside the window never reached a
  // listener bound to the canvas, so `isDragging` / `isPanning` stayed true,
  // the `.dragging` class stayed on the marker, the pre-drag snapshot was
  // never committed and zone auto-assignment never ran. Moving the pointer
  // back over the canvas then resumed dragging with no button held.
  // `onCanvasMouseUp` already guards on both flags, so it is safe to receive
  // events from anywhere. `pointercancel` covers the browser taking the
  // gesture away (touch scroll, a system gesture).
  window.addEventListener("mouseup", onCanvasMouseUp);
  window.addEventListener("pointercancel", onCanvasMouseUp);
  svg().addEventListener("wheel", onCanvasWheel, { passive: false });
  svg().addEventListener("contextmenu", (e: MouseEvent) => e.preventDefault());
  document.addEventListener("keydown", onKeyDown);
  document.addEventListener("keyup", (e) => {
    if (e.key === " ") {
      state.spaceHeld = false;
      // No isPanning guard needed — `.is-panning` outranks `.is-pannable`
      // in canvas.css, so an in-flight pan keeps its `grabbing` cursor.
      container().classList.remove("is-pannable");
    }
  });

  // Minimum safe dimensions — guards against zoomToFit / snapMeters blowing
  // up when the user clears the input (empty string → NaN) or types 0.
  const MIN_PLOT_DIM = 1;       // meters
  const MIN_PX_PER_METER = 1;
  const MIN_GRID_SIZE = 0.05;   // meters (matches <input min>)

  // Typed key tuple in lieu of `(state as any)[id] = next` (#1494). Narrowed
  // to the three numeric-state fields this widget mutates; the compiler
  // refuses any other key, so a rename in state.ts surfaces here.
  const NUMERIC_INPUT_IDS = ["plotWidth", "plotHeight", "pxPerMeter"] as const;
  type NumericInputId = typeof NUMERIC_INPUT_IDS[number];

  NUMERIC_INPUT_IDS.forEach((id: NumericInputId) => {
    const minValue = id === "pxPerMeter" ? MIN_PX_PER_METER : MIN_PLOT_DIM;
    $(`#${id}`).addEventListener("change", (e: Event) => {
      const input = e.target as HTMLInputElement;
      // The plot size is a length in the user's units; the scale is px per metre.
      const parsed = id === "pxPerMeter" ? parseFloat(input.value) : readField(input, "length");
      const next = Number.isFinite(parsed) && parsed >= minValue
        ? parsed
        : minValue;
      state[id] = next;
      if (id === "pxPerMeter") input.value = String(next);
      else showField(input, next, "length");
      zoomToFit(); renderAll();
    });
  });

  $("#gridSize").addEventListener("change", (e: Event) => {
    const input = e.target as HTMLInputElement;
    const parsed = parseFloat(input.value);
    const next = Number.isFinite(parsed) && parsed >= MIN_GRID_SIZE
      ? parsed
      : MIN_GRID_SIZE;
    state.gridSize = next;
    input.value = String(next);
    renderAll();
  });
  // The Show Grid toggle moved to the Layers flyout in #1205
  // (`#pl2-layer-grid`); chrome.ts wires the change handler against
  // state.showGrid and re-runs renderGrid(). No change handler here.
  $("#snapToGrid").addEventListener("change", (e: Event) => { state.snapToGrid = (e.target as HTMLInputElement).checked; });

  // Unified coverage view selector lives in the Layers flyout.
  // Replaces the old context-bar `#showCoverage` + `#showHeatmap`
  // checkbox pair (#1204).
  const coverageViewControl = document.getElementById("pl2-layer-coverage-view");
  if (coverageViewControl) {
    coverageViewControl.addEventListener("change", (e: Event) => {
      const value = (e.target as HTMLSelectElement).value;
      if (value === 'off' || value === 'rings' || value === 'heatmap') {
        state.coverageView = value;
        renderAll();
      }
    });
  }

  // Toolbar model/arc
  $("#activeModel").addEventListener("change", (e: Event) => { state.activeModel = (e.target as HTMLInputElement).value; updateToolbarPreset(); });
  $("#activeArc").addEventListener("change", (e: Event) => {
    state.activeArc = parseInt((e.target as HTMLSelectElement).value) || state.activeArc;
    updateToolbarPreset();
  });

  // Properties
  ["sprinklerModel", "sprinklerName", "sprinklerArc", "sprinklerX",
   "sprinklerY", "sprinklerRadius", "sprinklerRotation", "sprinklerZone", "sprinklerBody"].forEach((id) => {
    $(`#${id}`).addEventListener("change", applyPropertyChange);
  });

  ["zoneName", "zoneValve"].forEach((id) => {
    $(`#${id}`).addEventListener("change", applyZonePropertyChange);
  });

  ["pipeName", "pipeDiameter", "pipeZone"].forEach((id) => {
    $(`#${id}`).addEventListener("change", applyPipePropertyChange);
  });

  ["treeName", "treeType", "treeX", "treeY", "treeRadius", "objWidth", "objLength", "objRotation", "wsFlowRate", "wsPressure", "wsSourceType", "wsElevation"].forEach((id) => {
    const el = $(`#${id}`);
    if (el) el.addEventListener("change", applyTreePropertyChange);
  });

  ["valveObjName", "valveObjX", "valveObjY", "valveObjModel", "valveObjZone"].forEach((id) => {
    $(`#${id}`).addEventListener("change", applyValveObjPropertyChange);
  });

  ["wsName", "wsX", "wsY", "wsModel"].forEach((id) => {
    const el = $(`#${id}`);
    if (el) el.addEventListener("change", applyWeatherStationPropertyChange);
  });

  ["dripLineName", "dripLineModel", "dripLineZone"].forEach((id) => {
    const el = $(`#${id}`);
    if (el) el.addEventListener("change", applyDripPropertyChange);
  });

  // Drip model toolbar
  const dripModelSel = $("#activeDripModel");
  if (dripModelSel) {
    buildDripModelSelect(dripModelSel, state.activeDripModel);
    dripModelSel.addEventListener("change", (e: Event) => {
      state.activeDripModel = (e.target as HTMLInputElement).value;
    });
  }

  // Pipe diameter toolbar
  $("#activePipeDiameter").addEventListener("change", (e: Event) => {
    state.activePipeDiameter = parseInt((e.target as HTMLInputElement).value);
  });

  // Guides
  $("#showGuides").addEventListener("change", (e: Event) => {
    state.showGuides = (e.target as HTMLInputElement).checked;
    renderAll();
  });

  // Double-click on canvas to add manual guide
  svg().addEventListener("dblclick", (e: MouseEvent) => {
    if (state.mode !== "select") return;
    const m = screenToMeters(e.clientX, e.clientY);
    const margin = 1.5; // meters from axis to trigger guide
    if (Math.abs(m.y) < margin && m.x >= 0) {
      // Near X axis — add vertical guide
      const snapped = snapMeters({ x: m.x, y: 0 });
      addGuide("x", snapped.x);
    } else if (Math.abs(m.x) < margin && m.y >= 0) {
      // Near Y axis — add horizontal guide
      const snapped = snapMeters({ x: 0, y: m.y });
      addGuide("y", snapped.y);
    }
  });

  // Actions
  $("#btnUndo").addEventListener("click", () => undo(renderAll));
  $("#btnRedo").addEventListener("click", () => redo(renderAll));
  $("#btnClear").addEventListener("click", async () => {
    if (!await showConfirm(t("dialogs.clearConfirm"))) return;
    saveState();
    state.boundary = []; state.sprinklers = []; state.zones = []; state.pipes = [];
    state.dripLines = [];
    state.trees = []; state.valveObjects = []; state.weatherStations = []; state.guides = [];
    clearSelection();
    state.nextSprinklerId = 1; state.nextZoneId = 1; state.nextPipeId = 1;
    state.nextDripLineId = 1;
    state.nextTreeId = 1; state.nextValveObjId = 1; state.nextWeatherStationId = 1; state.nextGuideId = 1;
    renderAll();
  });

  // Auto-place — the algorithm has always been in calculations/logic.ts and
  // unit-tested (ai-placement.test.js); the button just never had a handler,
  // so clicking AUTO did nothing at all (#1581).
  $("#btnAutoPlace")?.addEventListener("click", () => {
    if (state.boundary.length < 3) {
      showToast(tf("toolbar.autoPlaceNoBoundary", "Draw the property boundary first"), "warning");
      return;
    }

    // Landscape objects are obstacles to plant around — the two shapes line
    // up field for field, so they pass straight through.
    const placements = autoPlaceSprinklers({
      boundary: state.boundary,
      obstacles: state.trees,
      model: state.activeModel,
    });

    if (!placements.length) {
      showToast(tf("toolbar.autoPlaceNone", "No room for heads inside this boundary"), "warning");
      return;
    }

    // Auto-place is deterministic, so pressing it twice returned the same grid
    // and stacked a second head on every point — invisible on the canvas, but
    // it doubled the flow figure and the BOM. Keep it idempotent by skipping
    // points a head already sits on, which also preserves hand-placed heads.
    const fresh = placements.filter(
      (p) => !state.sprinklers.some((s) => Math.hypot(s.x - p.x, s.y - p.y) < OCCUPIED_TOLERANCE_M),
    );

    if (!fresh.length) {
      showToast(
        tf("toolbar.autoPlaceAllOccupied", "Every position already has a head"),
        "warning",
      );
      return;
    }

    saveState();
    for (const p of fresh) {
      // Mirror the shape addSprinklerAt() builds — a head without `radius`
      // renders no coverage ring and makes the uniformity figure NaN.
      state.sprinklers.push(createSprinkler({
        id: state.nextSprinklerId++,
        x: p.x,
        y: p.y,
        model: p.model,
        radius: p.radius,
        arc: p.arc,
        rotation: p.rotation,
      }));
    }
    autoAssignSprinklersToZones();
    renderAll();
    showToast(
      tf("toolbar.autoPlaceDone", "Heads placed: {count}", { count: fresh.length }),
      "success",
    );
  });

  $("#btnExportSVG").addEventListener("click", exportSVG);
  const btnExportPDF = $("#btnExportPDF");
  if (btnExportPDF) btnExportPDF.addEventListener("click", exportPDF);

  $("#btnZoomIn").addEventListener("click", () => zoomAt(container().clientWidth / 2, container().clientHeight / 2, 0.9));
  $("#btnZoomOut").addEventListener("click", () => zoomAt(container().clientWidth / 2, container().clientHeight / 2, 1.1));
  $("#btnZoomFit").addEventListener("click", zoomToFit);

  // Compass widget — restored in #1330 after user feedback. Was parked
  // in #1118 batch 2 ("deemed unnecessary"), but absence proved
  // disorienting for first-time users laying out boundaries. The
  // interactive widget reads `state.canvasRotation` and updates its
  // N/S/E/W labels via `updateCompassLabels()` on render.
  createCompass(container())

  // Auto-save to localStorage
  let lastSaveHash = "";
  let localAutoSaveTimer: unknown = null;

  function startLocalAutoSave() {
    if (localAutoSaveTimer) return;
    localAutoSaveTimer = setInterval(() => {
      const data = {
        version: PROJECT_DATA_VERSION, plotWidth: state.plotWidth, plotHeight: state.plotHeight,
        pxPerMeter: state.pxPerMeter, gridSize: state.gridSize,
        showGrid: state.showGrid, snapToGrid: state.snapToGrid,
        // New canonical field + legacy booleans for backward-compat (#1204).
        coverageView: state.coverageView,
        showCoverage: state.coverageView !== 'off',
        showHeatmap: state.coverageView === 'heatmap',
        showGuides: state.showGuides,
        activeModel: state.activeModel, activeArc: state.activeArc,
        activePipeDiameter: state.activePipeDiameter,
        activeDripModel: state.activeDripModel,
        boundary: state.boundary, sprinklers: state.sprinklers, zones: state.zones,
        pipes: state.pipes, dripLines: state.dripLines,
        trees: state.trees, valveObjects: state.valveObjects,
        weatherStations: state.weatherStations, guides: state.guides,
        nextSprinklerId: state.nextSprinklerId, nextZoneId: state.nextZoneId,
        nextPipeId: state.nextPipeId, nextDripLineId: state.nextDripLineId,
        nextTreeId: state.nextTreeId,
        nextValveObjId: state.nextValveObjId,
        nextWeatherStationId: state.nextWeatherStationId, nextGuideId: state.nextGuideId,
      };
      const json = JSON.stringify(data);
      if (json !== lastSaveHash) {
        localStorage.setItem(LOCAL_AUTOSAVE_KEY, json);
        lastSaveHash = json;
      }
    }, LOCAL_AUTOSAVE_INTERVAL_MS);
  }

  // Load project: first try API (?project=ID), then localStorage fallback
  const urlParams = new URLSearchParams(window.location.search);
  const projectId = urlParams.get("project");

  async function initProject() {
    // `initI18n` -> `loadLocale` throws on a non-2xx /api/v1/locale/:lang
    // response or a dropped connection. It used to reject straight out of
    // initProject() as an unhandled rejection, so setMode / zoomToFit /
    // renderAll / the autosave loop below never ran: the user got a blank
    // canvas, no error, and nothing they drew afterwards was ever saved.
    // Translations degrading to their raw keys is far better than that.
    try {
      await initI18n();
    } catch (err) {
      console.error("i18n init failed, continuing with untranslated UI:", err);
    }

    // Hydrate the active unit system once at boot — render.ts then reads
    // state.userUnits without re-querying window/localStorage per paint.
    // Per issue #1431 (Phase 1). Anonymous users default to 'metric'.
    state.userUnits = getUnits();
    applyFieldUnits();
    showPlotSize();

    // Initialize feedback and onboarding AFTER translations are loaded
    initFeedback();
    if (featureEnabled("planner_onboarding")) {
      startOnboarding();
    }

    // Rebuild dropdowns now that translations are loaded
    const activeObjSel = $("#activeObjectType");
    const treeTypeSel = $("#treeType");
    if (activeObjSel) buildObjectTypeSelect(activeObjSel, "tree");
    if (treeTypeSel) buildObjectTypeSelect(treeTypeSel, "tree");

    let loaded = false;

    if (projectId) {
      loaded = await loadProjectFromAPI(parseInt(projectId), {
        $, renderAll, updateToolbarPreset, zoomToFit,
        startCloudAutoSave: () => startCloudAutoSave(),
      });
    }

    if (!loaded) {
      // Fallback to localStorage autosave
      try {
        const saved = localStorage.getItem(LOCAL_AUTOSAVE_KEY);
        if (saved) {
          const data = JSON.parse(saved);
          state.plotWidth = data.plotWidth || 20;
          state.plotHeight = data.plotHeight || 15;
          state.pxPerMeter = data.pxPerMeter || 40;
          state.gridSize = data.gridSize || 0.5;
          state.boundary = data.boundary || [];
          // The same id renumbering, with references carried along, as the
          // API restore path: the local autosave mirrors whatever cloud
          // project was open, so it holds the same seeded or hostile ids.
          const stored = normalizeStoredElements(data) as Record<string, any[]>;
          // migrateSprinkler / migrateZone return the looser stored shape;
          // state.ts casts the same way on the API restore path.
          state.sprinklers = stored.sprinklers.map(migrateSprinkler) as typeof state.sprinklers;
          state.zones = stored.zones.map(migrateZone) as typeof state.zones;
          state.pipes = stored.pipes;
          state.dripLines = stored.dripLines;
          state.trees = stored.trees;
          state.valveObjects = stored.valveObjects;
          // weatherStations + nextWeatherStationId were missing from the
          // fallback restore path, so users who added weather stations in a
          // guest session lost them on reload even though the autosave
          // payload contained the data.
          state.weatherStations = stored.weatherStations;
          state.guides = data.guides || [];
          // Same recovery the API restore path does: a payload written before
          // a given counter was persisted must not reset it to 1, or the next
          // insert takes an id an existing element already owns.
          const localPart = (id: number) => (id || 0) % 10_000_000;
          const highest = <T extends { id: number }>(items: T[]) =>
            items.reduce((m, item) => Math.max(m, localPart(item.id)), 0);
          state.nextSprinklerId = data.nextSprinklerId || highest(state.sprinklers) + 1;
          state.nextZoneId = data.nextZoneId || highest(state.zones) + 1;
          state.nextPipeId = data.nextPipeId || highest(state.pipes) + 1;
          state.nextDripLineId = data.nextDripLineId || highest(state.dripLines) + 1;
          state.nextTreeId = data.nextTreeId || highest(state.trees) + 1;
          state.nextValveObjId = data.nextValveObjId || highest(state.valveObjects) + 1;
          state.nextWeatherStationId = data.nextWeatherStationId || highest(state.weatherStations) + 1;
          state.nextGuideId = data.nextGuideId || highest(state.guides) + 1;
          if (data.showGrid !== undefined) state.showGrid = data.showGrid;
          if (data.snapToGrid !== undefined) state.snapToGrid = data.snapToGrid;
          // New canonical field wins; fall back to legacy booleans (#1204).
          if (data.coverageView !== undefined) {
            state.coverageView = data.coverageView;
          } else if (data.showCoverage !== undefined || data.showHeatmap !== undefined) {
            state.coverageView = deriveCoverageView({
              showCoverage: data.showCoverage,
              showHeatmap: data.showHeatmap,
            });
          }
          if (data.showGuides !== undefined) state.showGuides = data.showGuides;
          if (data.activeModel) state.activeModel = data.activeModel;
          if (data.activeArc) state.activeArc = data.activeArc;
          if (data.activePipeDiameter) state.activePipeDiameter = data.activePipeDiameter;
          if (data.activeDripModel) state.activeDripModel = data.activeDripModel;
          showPlotSize();
          $("#pxPerMeter").value = state.pxPerMeter;
          $("#gridSize").value = state.gridSize;
          // The Show Grid toggle moved to the Layers flyout in #1205.
          // Sync into the new id; guard for unmounted flyout (tests).
          {
            const layerGridCb = document.getElementById("pl2-layer-grid");
            if (layerGridCb instanceof HTMLInputElement) {
              layerGridCb.checked = state.showGrid;
            }
          }
          $("#snapToGrid").checked = state.snapToGrid;
          syncCoverageViewControl();
          $("#showGuides").checked = state.showGuides;
          $("#activePipeDiameter").value = state.activePipeDiameter;
        }
      } catch { /* ignore */ }
    }

    updateToolbarPreset();
    setMode("select");
    zoomToFit();
    renderAll();

    // Start auto-save AFTER project is loaded
    startLocalAutoSave();
    if (getCurrentProjectId()) {
      startCloudAutoSave();
    } else {
      // Anonymous-URL session: defer draft creation until the user actually
      // mutates something (avoids POST /projects spam for every visit).
      // When the draft is created, promote to cloud autosave so the work is
      // not lost on tab close.
      //
      // The hook is one-shot, and a create that failed (a 429 from the
      // 30/hr rack-attack limit, a dropped connection, a guest who signs in
      // later) used to spend it for good: `currentProjectId` stayed null,
      // the autosave loop never started and nothing drawn afterwards reached
      // the server (#1779). Re-arm it so a later edit retries — but not
      // within DRAFT_RETRY_MS, or a rate-limited session would POST and
      // toast on every click.
      let retryAt = 0;
      const armDraftBootstrap = () => setFirstMutationHook(() => {
        if (Date.now() < retryAt) {
          armDraftBootstrap();
          return;
        }
        ensureCloudDraft()
          .then((id) => {
            if (id) {
              startCloudAutoSave();
              return;
            }
            retryAt = Date.now() + DRAFT_RETRY_MS;
            armDraftBootstrap();
          })
          // ensureCloudDraft swallows its own failures, but a throw inside
          // startCloudAutoSave would otherwise vanish as an unhandled
          // rejection with the user still believing their work is syncing.
          .catch((err) => console.error("Cloud draft bootstrap failed:", err));
      });
      armDraftBootstrap();
    }
  }

  initProject().catch((err) => console.error("Planner init failed:", err));
}

// Fire a final cloud save on page unload so a recent edit isn't lost if the
// tab is closed before the next 10s autosave tick. When the plan is too big
// to send during unload, ask the browser to confirm leaving; the save that
// flushCloudAutoSave started completes if the user stays.
window.addEventListener("beforeunload", (event) => {
  if (flushCloudAutoSave() === "unsent") {
    event.preventDefault();
    event.returnValue = "";
  }
});

// Expose setLanguage globally for inline onchange handlers
window.__smartpluvia_setLang = async (lang) => {
  const { setLanguage } = await import('../shared/i18n.ts');
  await setLanguage(lang);
  updateCompassLabels();
};

// Typed shape of the boundary point + sprinkler-add payloads templates and
// onboarding hand to the planner. Replaces the prior `points: any` /
// `sprinklers: any` annotations (#1494). External callers must conform; if
// they don't, the compiler catches it.
interface TemplateBoundaryPoint { x: number; y: number }
interface TemplateSprinklerInput {
  x: number;
  y: number;
  model?: string;
  arc?: number;
  rotation?: number;
  radius?: number;
  zoneId?: number | null;
}

// Expose API for external modules (templates, onboarding)
window.__smartpluvia = {
  applyBoundary(points: TemplateBoundaryPoint[]) {
    saveState();
    state.boundary = points.map((p) => ({ x: p.x, y: p.y }));
    // Adjust plot dimensions to fit the template
    const maxX = Math.max(...points.map((p) => p.x));
    const maxY = Math.max(...points.map((p) => p.y));
    state.plotWidth = Math.max(state.plotWidth, Math.ceil(maxX + 2));
    state.plotHeight = Math.max(state.plotHeight, Math.ceil(maxY + 2));
    showPlotSize();
    // renderAll not available at module parse time, but applyBoundary is only called after init
    renderGrid(); renderManualGuides(); renderBoundary(); renderCoverage(); renderHeatmap();
    renderZones(); renderPipes(); renderDripLines(); renderTrees(); renderValveObjects();
    renderWeatherStations(); renderSprinklers(); renderTemp(); updateStats();
    zoomToFit();
  },
  getState() {
    return {
      plotWidth: state.plotWidth,
      plotHeight: state.plotHeight,
      pxPerMeter: state.pxPerMeter,
      boundary: state.boundary,
      sprinklers: state.sprinklers,
      zones: state.zones,
      pipes: state.pipes,
      trees: state.trees,
      valveObjects: state.valveObjects,
      dripLines: state.dripLines,
      weatherStations: state.weatherStations,
      // Which element is selected is state too, and the blueprint chrome
      // needs it to reveal the matching properties panel. Read-only here:
      // mutating these has no effect, selection is driven by events.ts.
      selectedSprinkler: state.selectedSprinkler,
      selectedZone: state.selectedZone,
      selectedPipe: state.selectedPipe,
      selectedDripLine: state.selectedDripLine,
      selectedValveObj: state.selectedValveObj,
      selectedWeatherStation: state.selectedWeatherStation,
      selectedTree: state.selectedTree,
    };
  },
  getProjectId() { return getCurrentProjectId(); },
  saveToCloud() { return saveProjectToAPI(); },
  addSprinklers(sprinklers: TemplateSprinklerInput[]) {
    saveState();
    const added = sprinklers.map((sp) => createSprinkler({
      id: state.nextSprinklerId++,
      x: sp.x,
      y: sp.y,
      model: sp.model || state.activeModel,
      arc: sp.arc,
      rotation: sp.rotation,
      // Auto-placement computes a throw radius per head; without this the
      // catalog value for the arc silently replaced it.
      radius: sp.radius,
      // A proposed grouping is only meaningful if the zone exists — an
      // unresolvable id would leave the head pointing at nothing, which
      // reads worse downstream than plain "unzoned".
      zoneId: state.zones.some((z) => z.id === sp.zoneId) ? sp.zoneId : null,
    }));
    // A head that names no zone of this plan takes the one it stands in, as a
    // pasted or clicked head does, rather than staying unzoned.
    zoneByPosition(added.filter((sp) => sp.zoneId === null), state.zones);
    state.sprinklers.push(...added);
    renderCoverage(); renderSprinklers(); updateStats();
  },
};

// Load API-driven catalogs AND feature flags in parallel, then initialize
// the planner. Awaiting both avoids a race where featureEnabled() returns
// false for the first ~100ms of a session — which would make Ctrl+V,
// multi-select and the shortcut help modal appear disabled to early users.
Promise.allSettled([loadCatalogs(), loadFeatureFlags()])
  .finally(() => init());
